import { Check, Close, EditCalendar } from "@mui/icons-material";
import { Alert, IconButton, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { useCallback, useState } from "react";
import { useUpdateScheduledReactivationMutation } from "src/api/services/subscriptionsApi";
import { SubscriptionReactivation } from "src/api/types";
import DatePicker from "src/components/DatePicker";
import { useSentryLogger } from "src/hooks/useSentryLogger";

interface Props {
  scheduledReactivation: SubscriptionReactivation;
}

const ScheduledReactivationAlert = ({ scheduledReactivation }: Props) => {
  const [openCalendar, setOpenCalendar] = useState<boolean>(false);
  const [date, setDate] = useState<Date | null>(
    new Date(scheduledReactivation?.scheduled_for || ""),
  );
  const [updateScheduledReactivation] =
    useUpdateScheduledReactivationMutation();
  const errorLog = useSentryLogger("error");

  const handleCalendar = useCallback(() => {
    setOpenCalendar((open) => !open);
  }, []);

  const handleUpdate = useCallback(async () => {
    if (date == null) return;

    try {
      await updateScheduledReactivation({
        id: Number(scheduledReactivation.resource_id),
        body: {
          scheduled_for: date.toISOString(),
        },
      });
      setOpenCalendar(false);
    } catch (error) {
      errorLog(`Unable to update scheduled reactivation action`, error);
    }
  }, [
    date,
    errorLog,
    scheduledReactivation.resource_id,
    updateScheduledReactivation,
  ]);

  return (
    <Alert severity="warning" sx={{ width: "100%" }}>
      {!openCalendar ? (
        <Stack direction={"row"}>
          <Typography>{`This subscription is scheduled for reactivation on ${format(
            new Date(scheduledReactivation.scheduled_for),
            "MMMM dd, yyyy",
          )}.`}</Typography>
          <IconButton size="small" onClick={handleCalendar}>
            <EditCalendar />
          </IconButton>
        </Stack>
      ) : (
        <Stack direction={"row"} spacing={0.5}>
          <DatePicker
            slots={{ openPickerIcon: EditCalendar }}
            minDate={new Date()}
            fullWidth={false}
            onChange={setDate}
            value={date}
          />
          <Stack direction={"row"}>
            <IconButton size="small" onClick={handleUpdate}>
              <Check />
            </IconButton>
            <IconButton size="small" onClick={handleCalendar}>
              <Close />
            </IconButton>
          </Stack>
        </Stack>
      )}
    </Alert>
  );
};

export default ScheduledReactivationAlert;
